import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import {
  FaComments,
  FaUser,
  FaRobot,
  FaWhatsapp,
  FaInstagram,
  FaFacebook,
  FaSearch,
  FaTimes,
  FaSpinner
} from 'react-icons/fa';
import './WorkflowChat.css';

const WorkflowChat = ({ workflowId, onClose }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (workflowId) {
      fetchConversations();
    }
  }, [workflowId, filterPlatform]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation._id);
      // Poll for new messages every 3 seconds
      const interval = setInterval(() => {
        fetchMessages(selectedConversation._id, true);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const platformQuery = filterPlatform !== 'all' ? `&platform=${filterPlatform}` : '';
      const response = await axios.get(
        `${API_URL}/api/conversations/workflow/${workflowId}?limit=100${platformQuery}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setConversations(response.data.data || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId, silent = false) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/conversations/${conversationId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setMessages(response.data.data?.messages || []);
    } catch (error) {
      if (!silent) {
        console.error('Error fetching messages:', error);
        toast.error('Failed to load messages');
      }
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'whatsapp':
        return <FaWhatsapp style={{ color: '#25D366' }} />;
      case 'instagram':
        return <FaInstagram style={{ color: '#E4405F' }} />;
      case 'facebook':
        return <FaFacebook style={{ color: '#1877F2' }} />;
      default:
        return <FaComments />;
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = 
      conv.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.contactId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.phoneNumber?.includes(searchTerm);
    return matchesSearch;
  });

  return (
    <div className="workflow-chat-container">
      <div className="workflow-chat-header">
        <h3>
          <FaComments style={{ marginRight: '10px' }} />
          Workflow Conversations
        </h3>
        <button className="btn-icon" onClick={onClose}>
          <FaTimes />
        </button>
      </div>

      <div className="workflow-chat-content">
        {/* Conversations List */}
        <div className="conversations-sidebar">
          <div className="conversations-filters">
            <div className="search-box">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="platform-filters">
              <button
                className={`filter-btn ${filterPlatform === 'all' ? 'active' : ''}`}
                onClick={() => setFilterPlatform('all')}
              >
                All
              </button>
              <button
                className={`filter-btn ${filterPlatform === 'whatsapp' ? 'active' : ''}`}
                onClick={() => setFilterPlatform('whatsapp')}
              >
                <FaWhatsapp /> WhatsApp
              </button>
              <button
                className={`filter-btn ${filterPlatform === 'instagram' ? 'active' : ''}`}
                onClick={() => setFilterPlatform('instagram')}
              >
                <FaInstagram /> Instagram
              </button>
              <button
                className={`filter-btn ${filterPlatform === 'facebook' ? 'active' : ''}`}
                onClick={() => setFilterPlatform('facebook')}
              >
                <FaFacebook /> Facebook
              </button>
            </div>
          </div>

          <div className="conversations-list">
            {loading ? (
              <div className="loading-state">
                <FaSpinner className="spinner" />
                <p>Loading conversations...</p>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="empty-state">
                <FaComments />
                <p>No conversations yet</p>
                <small>Messages will appear here when workflows are triggered</small>
              </div>
            ) : (
              filteredConversations.map((conv) => (
                <div
                  key={conv._id}
                  className={`conversation-item ${selectedConversation?._id === conv._id ? 'active' : ''}`}
                  onClick={() => setSelectedConversation(conv)}
                >
                  <div className="conversation-avatar">
                    {getPlatformIcon(conv.platform)}
                  </div>
                  <div className="conversation-info">
                    <div className="conversation-header">
                      <span className="conversation-name">
                        {conv.contactName || conv.contactId || 'Unknown Contact'}
                      </span>
                      <span className="conversation-time">
                        {formatTime(conv.lastMessageAt)}
                      </span>
                    </div>
                    <div className="conversation-preview">
                      {conv.messages && conv.messages.length > 0
                        ? conv.messages[conv.messages.length - 1].content.substring(0, 50) + '...'
                        : 'No messages'}
                    </div>
                    <div className="conversation-meta">
                      <span className="conversation-platform">{conv.platform}</span>
                      {conv.phoneNumber && (
                        <span className="conversation-phone">{conv.phoneNumber}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Messages View */}
        <div className="messages-view">
          {selectedConversation ? (
            <>
              <div className="messages-header">
                <div className="messages-contact-info">
                  <div className="contact-avatar">
                    {getPlatformIcon(selectedConversation.platform)}
                  </div>
                  <div>
                    <h4>{selectedConversation.contactName || selectedConversation.contactId || 'Unknown Contact'}</h4>
                    <small>
                      {selectedConversation.platform}
                      {selectedConversation.phoneNumber && ` • ${selectedConversation.phoneNumber}`}
                    </small>
                  </div>
                </div>
              </div>

              <div className="messages-list">
                {messages.length === 0 ? (
                  <div className="empty-messages">
                    <FaComments />
                    <p>No messages in this conversation</p>
                  </div>
                ) : (
                  messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`message-item ${msg.role === 'user' ? 'user-message' : 'ai-message'}`}
                    >
                      <div className="message-avatar">
                        {msg.role === 'user' ? (
                          <FaUser />
                        ) : (
                          <FaRobot />
                        )}
                      </div>
                      <div className="message-content">
                        <div className="message-text">{msg.content}</div>
                        <div className="message-meta">
                          <span className="message-time">{formatTime(msg.timestamp)}</span>
                          {msg.role === 'ai' && msg.metadata?.provider && (
                            <span className="message-provider">via {msg.metadata.provider}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>
            </>
          ) : (
            <div className="no-conversation-selected">
              <FaComments />
              <p>Select a conversation to view messages</p>
              <small>All incoming and outgoing messages from workflows will appear here</small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowChat;

